// Normalization to GraphEdge (ARCHITECTURE.md §11.3 step 4, §11.7).
//
// Converts LLM CandidateProposals into the same `GraphEdge` type used by
// deterministic edges, with `classification: "candidate"`. Every edge gets an
// `llm_semantic_link` evidence entry carrying the LLM's reasoning, followed by
// one evidence entry per LlmEvidencePoint. The LLM's self-assessed confidence is
// propagated to every evidence entry, keeping the provenance auditable and
// reproducible (§11.8).

import type { Evidence } from "../evidence/types.js";
import type { GraphEdge } from "../types.js";
import type { CandidateProposal } from "./types.js";

export interface NormalizedCandidates {
  edges: GraphEdge[];
  evidence: Evidence[];
}

/** Build the evidence array for a single proposal. */
function evidenceForProposal(proposal: CandidateProposal): Evidence[] {
  const evidence: Evidence[] = [
    {
      type: "llm_semantic_link",
      detail: proposal.reasoning,
      confidence: proposal.confidence,
    },
  ];
  for (const point of proposal.evidencePoints) {
    const entry: Evidence = {
      type: point.type,
      detail: point.detail,
      confidence: proposal.confidence,
    };
    if (point.line !== undefined) entry.line = point.line;
    evidence.push(entry);
  }
  return evidence;
}

/**
 * Normalize candidate proposals to graph edges. Returns the candidate edges and
 * the flattened union of all evidence produced (the same entries are embedded in
 * each edge's `evidence` array). Each edge is classified `"candidate"`.
 */
export function normalizeCandidates(
  proposals: CandidateProposal[],
): NormalizedCandidates {
  const edges: GraphEdge[] = [];
  const evidence: Evidence[] = [];

  for (const proposal of proposals) {
    const edgeEvidence = evidenceForProposal(proposal);
    edges.push({
      from: proposal.from,
      to: proposal.to,
      kind: proposal.edgeKind,
      classification: "candidate",
      evidence: edgeEvidence,
    });
    evidence.push(...edgeEvidence);
  }

  return { edges, evidence };
}
